import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  HiOutlineSquares2X2,
  HiOutlineUsers,
  HiOutlineRectangleStack,
  HiOutlineShoppingBag,
  HiOutlineBriefcase,
  HiOutlineDocumentText,
  HiOutlineInbox,
  HiOutlineBanknotes,
  HiOutlineUser,
  HiOutlineBars3,
  HiOutlineXMark,
  HiOutlineChatBubbleLeftRight,
} from "react-icons/hi2";
import Logo from "../assets/images/Logo.png";

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick?: () => void;
}

const NavItem = ({ to, icon, label, active, onClick }: NavItemProps) => (
  <Link to={to} onClick={onClick}>
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors text-sm font-medium ${
        active
          ? "bg-[#B51D28] text-white"
          : "text-[#1A1A1A] hover:bg-[#F0F0F0]"
      }`}
    >
      <span className="shrink-0">{icon}</span>
      <span>{label}</span>
    </div>
  </Link>
);

const navItems = [
  { to: "/", label: "Dashboard", icon: <HiOutlineSquares2X2 size={20} /> },
  { to: "/customers", label: "Customers", icon: <HiOutlineUsers size={20} /> },
  { to: "/services", label: "Services", icon: <HiOutlineRectangleStack size={20} /> },
  { to: "/gigs", label: "Gigs", icon: <HiOutlineBriefcase size={20} /> },
  { to: "/offers", label: "Offers", icon: <HiOutlineDocumentText size={20} /> },
  { to: "/proposals", label: "Proposals", icon: <HiOutlineInbox size={20} /> },
  { to: "/orders", label: "Orders", icon: <HiOutlineShoppingBag size={20} /> },
  { to: "/withdraw", label: "Withdraw", icon: <HiOutlineBanknotes size={20} /> },
  { to: "/chat", label: "Chat", icon: <HiOutlineChatBubbleLeftRight size={20} /> },
  { to: "/profile", label: "Profile", icon: <HiOutlineUser size={20} /> },
];

export const SideBar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  const navList = (onLinkClick?: () => void) => (
    <nav className="flex flex-col gap-1 px-3">
      {navItems.map((item) => (
        <NavItem
          key={item.to}
          to={item.to}
          icon={item.icon}
          label={item.label}
          active={isActive(item.to)}
          onClick={onLinkClick}
        />
      ))}
    </nav>
  );

  return (
    <>
      {/* Mobile toggle button (top-left, fixed) */}
      <button
        type="button"
        className="md:hidden fixed top-3 left-3 z-50 p-2 rounded-lg bg-white border border-[#E0E0E0] shadow-[0_1px_4px_rgba(0,0,0,0.06)]"
        onClick={() => setMobileOpen(true)}
        aria-label="Open menu"
      >
        <HiOutlineBars3 size={20} className="text-[#1A1A1A]" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar (desktop + mobile drawer) */}
      <aside
        className={`fixed md:sticky md:top-0 z-40 md:z-auto h-screen w-60 shrink-0 bg-white border-r border-[#E0E0E0] transition-transform duration-200 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="h-full flex flex-col py-6">
          <div className="flex items-center justify-between px-5 mb-8">
            <Link to="/" className="flex items-center gap-2">
              <img src={Logo} alt="TieUp Admin" className="w-10 h-10 object-contain" />
              <span className="text-base font-bold text-[#1A1A1A]">TieUp</span>
            </Link>
            <button
              type="button"
              className="md:hidden p-1 rounded hover:bg-[#F0F0F0]"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <HiOutlineXMark size={20} className="text-[#1A1A1A]" />
            </button>
          </div>
          {navList(() => setMobileOpen(false))}
        </div>
      </aside>
    </>
  );
};
